﻿import { WindowMWF } from "../../namespace-globals";
import { v4 } from "uuid";

export class DialogBinding {
  /** Type of dialog. There are currently three types: "default", "scroll", and "static". "Scroll" gets the 'X' icon to cancel the dialog, along with a scrollable content window. "Static" uses the lightbox style from the MWF site. */
  public type = "@";

  /** The title to appear in the dialog's header */
  public title = "@";

  /** Text of the button which triggers the dialog (Used when the hideDefaultButton is true) */
  public buttonText = "@?";

  /** Aria-Text of the button which triggers the dialog (Used when the hideDefaultButton is true) */
  public setAriaLabel = "@?";

  /** Whether to show the default button trigger */
  public hideDefaultButton = "<";

  /** Relative element id to be set on the dialog content when the defaultButton is not used */
  public elementId = "@?";

  /** Whether to show the default cancel or "X" button to close the dialog */
  public hideDefaultCancel = "<?";

  /** Callback function invoked when the dialog is initialized. Argument is the bound MWF dialog controller, and looks like: {controller: any} */
  public onDialogInitialized = "&?";

  /** Callback function invoked when the built-in hide function is clicked. Argument is the $event from the original click, and looks like: { $event: ng.IAngularEvent } */
  public onHideClicked = "&?";

  /** The area identifier for all business intelligence (BI) actions triggered by this component.*/
  public biArea = "@?";
}

/**
 * The Controller class for Dialog component
 * NOTE: There is an issue with the close button on this dialog which would get fixed once the latest MWF scripts are used
 */
export class DialogController {
  private controlInstance: any;
  private controlElement: Element;
  private type: string;
  private classType = "";
  private bodyClassType: string;
  private onDialogInitialized: (argument: { controller: any }) => void;
  public buttonText: string;
  public setAriaLabel: string;
  public hideDefaultButton: boolean;
  public hideDefaultCancel: boolean;
  public elementId: string;
  public biArea: string;
  // @ngInject
  constructor(
    private $element: ng.IAugmentedJQuery,
    private $window: WindowMWF,
    private $timeout: ng.ITimeoutService
  ) {}

  public $onChanges(changesObj: any): void {
    this.refreshView();
  }

  public $onInit(): void {
    this.refreshView();
    // set the relative id"s here programmatically.
    // reason: The interpolation variables are not resolved by the time linker is called which results in MWF not finding the correct element id's
    this.controlElement = this.$element[0].getElementsByClassName("dialog-holder").item(0);
    if (this.hideDefaultButton) {
      // set the relative element id for dialog
      this.$element[0].getElementsByClassName("dialog-holder").item(0).id = this.elementId;
    } else {
      let uniqueId = "sd" + v4(); // need a character in the beginning for well formed HTML
      this.$element[0].getElementsByClassName("dialog-trigger").item(0).setAttribute("data-js-dialog-show", uniqueId);
      this.controlElement.id = uniqueId;
      }

    if (this.buttonText != "" && this.setAriaLabel == "") {
      this.setAriaLabel = this.buttonText;
    }

    if (!this.elementId) {
      this.elementId = "sd-dialog-" + v4();
    }
    // using $timeout to solve the issue where the bindings are not resolved when this component is wrapped inside another component
    this.$timeout(() => {
      this.controlInstance = new this.$window.mwf["Dialog"](this.controlElement);
      this.controlInstance.unObserve();

      let observerConfig = {
        childList: true,
        subtree: true
      };
      this.controlInstance.modernObserver = new MutationObserver((mutations) => {
        // 3 levels of depth was chosen by inspection, this may change with MWF upgrades. This is considered
        //  the "reasonable" DOM distance between MWF stuff and non-MWF (our) stuff.
        mutations = mutations.filter((x) => this.isClassInParentElementAtDepth("c-dialog", <any>x.target, 3));
        if (mutations.length > 0) {
          this.controlInstance.onModernMutations(mutations);
        }
      });
      this.controlInstance.modernObserver.observe(this.controlInstance.element, observerConfig);

      if (this.onDialogInitialized) {
        this.onDialogInitialized({ controller: this.controlInstance });
      }
    }, 0);
  }

  isClassInParentElementAtDepth(className: string, element: Element, levels: number): boolean {
    if (levels == 0 || element == undefined) {
      return false;
    }

    if (element.className.includes(className)) {
      return true;
    }

    return this.isClassInParentElementAtDepth(className, element.parentElement, levels - 1);
  }

  // Method to set the appropriate classes based on the type of alert
  private refreshView(): void {
    if (angular.isUndefined(this.type)) {
      this.type = "default";
    }
    switch (this.type.toLowerCase()) {
      case "default":
        break;
      case "scroll":
        this.classType = "f-flow";
        this.bodyClassType = "f-dialog-scroll";
        break;
      case "static":
        this.classType = "f-lightbox";
        break;
      default:
        throw new Error(`Invalid type binding specified: ${this.type}`);
    }
    if (this.hideDefaultButton && !this.elementId) {
      throw new Error(`Element Id is needed when the default button is not set`);
    }
  }
}

export const sdDialog: ng.IComponentOptions = {
  controller: DialogController,
  templateUrl: "../ShellCommon/Controls/dialog/dialog.html",
  bindings: <any>new DialogBinding(),
  transclude: {
    body: "sdDialogBody",
    footer: "?sdDialogFooter"
  }
};
